import React, { useState, useEffect } from 'react';
import { Send, Layout, MessageSquare, History, Smartphone, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import api from '../../services/api';
import alertService from '../../utils/alert';
import DashboardModuleShell from '../../components/DashboardModuleShell';

const MetaDirectMessagePage = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [message, setMessage] = useState({
    recipient: '',
    type: 'template',
    text: '',
    templateName: '',
    language: 'en_US',
    variables: {} // Key: 1, 2, 3...
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/meta/templates');
      const approved = (res.data.templates || []).filter(t => t.status === 'APPROVED');
      setTemplates(approved);
      if (approved.length > 0) {
        handleTemplateSelect(approved[0]);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleTemplateSelect = (t) => {
    setSelectedTemplate(t);
    setMessage(prev => ({
      ...prev,
      templateName: t.name,
      language: t.language,
      variables: {}
    }));
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.recipient) return alertService.toast('error', 'Recipient is required');

    setLoading(true);
    try {
      // Construct components from variables
      const components = [];
      const bodyVars = Object.keys(message.variables)
        .sort((a, b) => a - b)
        .map(key => ({ type: 'text', text: message.variables[key] }));

      if (bodyVars.length > 0) {
        components.push({
          type: 'body',
          parameters: bodyVars
        });
      }

      const payload = {
        recipient: message.recipient,
        type: message.type,
        text: message.text,
        templateName: message.templateName,
        language: message.language,
        components
      };

      await api.post('/meta/send', payload);
      alertService.toast('success', 'Message sent successfully');
      // fetchHistory();
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  // Extract variables from template body
  const getVariablesCount = () => {
    if (!selectedTemplate) return 0;
    const body = selectedTemplate.components.find(c => c.type === 'BODY')?.text || '';
    const matches = body.match(/\{\{\d+\}\}/g);
    return matches ? new Set(matches).size : 0;
  };

  const varsCount = getVariablesCount();

  const stats = [
    { label: 'Direct Messages', value: 'Live', icon: Send, meta: 'Status' },
    { label: 'Templates', value: templates.length, icon: Layout },
    { label: 'Daily Limit', value: '1,000+', icon: History, meta: 'Tier 1' },
  ];

  return (
    <DashboardModuleShell
      title="Direct Message"
      subtitle="Send WhatsApp messages to individual recipients using approved templates or session text."
      icon={MessageSquare}
      stats={stats}
    >
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Left: Composer */}
        <div className="xl:col-span-2 space-y-6">
          <div className="sf-card">
            <div className="sf-card-header">
              <h3 className="sf-card-title flex items-center gap-2">
                <Send className="w-4 h-4 text-[#0176D3]" />
                Send Message
              </h3>
              <div className="flex gap-2">
                {['template', 'text'].map(t => (
                  <button
                    key={t}
                    onClick={() => setMessage({ ...message, type: t })}
                    className={cn(
                      "px-3 h-7 rounded-md text-[11px] font-bold uppercase transition-colors capitalize",
                      message.type === t ? "bg-[#0176D3] text-white" : "bg-white text-[#706E6B] border border-[#D8DDE6] hover:bg-[#F4F6F9]"
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSend} className="sf-card-body space-y-6">
              <div className="space-y-2">
                <label className="text-[11px] font-bold text-[#706E6B] block">Recipient (Mobile Number)</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 text-[#706E6B] font-bold text-[13px]">+</div>
                  <input
                    type="text"
                    placeholder="919876543210"
                    value={message.recipient}
                    onChange={(e) => setMessage({ ...message, recipient: e.target.value.replace(/\D/g, '') })}
                    className="w-full pl-7 pr-3 h-10 bg-white border border-[#D8DDE6] rounded-md text-[13px] font-medium outline-none focus:border-[#0176D3] focus:ring-2 focus:ring-[#0176D3]/10 transition-colors"
                  />
                </div>
              </div>

              {message.type === 'text' ? (
                <div className="space-y-3">
                  <label className="text-[11px] font-bold text-[#706E6B] block">Message Text</label>
                  <textarea
                    rows={6}
                    value={message.text}
                    onChange={(e) => setMessage({ ...message, text: e.target.value })}
                    placeholder="Enter your message... Note: Session text requires an active 24-hour window."
                    className="w-full px-3 py-3 bg-white border border-[#D8DDE6] rounded-md text-[13px] font-medium outline-none focus:border-[#0176D3] focus:ring-2 focus:ring-[#0176D3]/10 transition-colors resize-none leading-relaxed"
                  />
                  <div className="p-3 bg-[#FFF4E5] border border-[#FFCD94] rounded-md flex gap-2.5">
                    <AlertCircle className="text-[#B35F00] shrink-0 w-4 h-4 mt-0.5" />
                    <p className="text-[11px] text-[#B35F00] font-medium leading-relaxed">
                      Session messages can only be sent if the recipient has messaged you in the last 24 hours. Use templates for business-initiated conversations.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                   <div className="space-y-3">
                    <label className="text-[11px] font-bold text-[#706E6B] block">Select Template</label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {templates.map(t => (
                        <div
                          key={t._id}
                          onClick={() => handleTemplateSelect(t)}
                          className={cn(
                            "p-3 border rounded-md cursor-pointer transition-colors relative",
                            message.templateName === t.name ? "bg-[#EAF4FE] border-[#0176D3]" : "bg-white border-[#D8DDE6] hover:border-[#0176D3]"
                          )}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <h4 className={cn("text-[13px] font-bold truncate pr-4", message.templateName === t.name ? "text-[#0176D3]" : "text-[#080707]")}>{t.name}</h4>
                            {message.templateName === t.name && <CheckCircle size={14} className="text-[#0176D3] shrink-0" />}
                          </div>
                          <p className="text-[10px] text-[#706E6B] font-bold uppercase tracking-wide">{t.language} &bull; {t.category}</p>
                        </div>
                      ))}
                      {templates.length === 0 && (
                        <div className="col-span-full py-6 text-center text-[12px] text-[#706E6B] font-medium">
                          No approved templates available.
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedTemplate && varsCount > 0 && (
                    <div className="space-y-3 p-4 bg-[#F4F6F9] rounded-md border border-[#D8DDE6]">
                      <div className="flex items-center gap-2">
                        <Info size={14} className="text-[#0176D3]" />
                        <h4 className="text-[11px] font-bold text-[#080707] uppercase tracking-wide">Template Variables Required</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Array.from({ length: varsCount }).map((_, i) => (
                          <div key={i} className="space-y-1">
                            <label className="text-[10px] font-bold text-[#706E6B]">Variable {"{{" + (i + 1) + "}}"}</label>
                            <input
                              type="text"
                              value={message.variables[i + 1] || ''}
                              onChange={(e) => setMessage({
                                ...message,
                                variables: { ...message.variables, [i + 1]: e.target.value }
                              })}
                              placeholder={`Enter value for {{${i + 1}}}`}
                              className="w-full px-3 h-9 bg-white border border-[#D8DDE6] rounded-md text-[13px] font-medium outline-none focus:border-[#0176D3] transition-colors"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedTemplate && (
                    <div className="space-y-2">
                      <label className="text-[11px] font-bold text-[#706E6B] block">Preview</label>
                      <div className="p-4 bg-[#F4F6F9] border border-[#D8DDE6] rounded-md italic text-[13px] text-[#706E6B] leading-relaxed font-medium">
                        {selectedTemplate.components.find(c => c.type === 'BODY')?.text.split(/(\{\{\d+\}\})/).map((part, i) => (
                           part.match(/\{\{\d+\}\}/)
                            ? <span key={i} className="text-[#0176D3] font-bold mx-1 not-italic">{message.variables[part.match(/\d+/)[0]] || part}</span>
                            : part
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="pt-6 border-t border-[#D8DDE6] flex justify-end">
                <Button
                  type="submit"
                  loading={loading}
                  className="h-10 px-6 rounded-md font-bold text-[13px] bg-[#0176D3] hover:bg-[#014486] transition-colors"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Message
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Right: History & Info */}
        <div className="space-y-6">
          <div className="sf-card">
            <div className="sf-card-header">
              <h3 className="sf-card-title">Recent Message Logs</h3>
              <History className="w-4 h-4 text-[#706E6B]" />
            </div>
            <div className="sf-card-body">
                <div className="py-10 text-center border border-dashed border-[#D8DDE6] rounded-md">
                  <Smartphone className="w-8 h-8 text-[#D8DDE6] mx-auto mb-3" />
                  <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-wide">No recent activity</p>
                </div>
            </div>
          </div>

          <div className="sf-card">
            <div className="sf-card-header">
              <h3 className="sf-card-title flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-[#2E844A]" />
                Meta Cloud API Status
              </h3>
              <span className="sf-badge sf-badge-success">Active</span>
            </div>
            <div className="sf-card-body space-y-4">
              <p className="text-[12px] text-[#706E6B] leading-relaxed font-medium">
                Your official Meta WhatsApp module is connected to the Graph API for reliable message delivery.
              </p>
              <div className="grid grid-cols-2 gap-3">
                 <div className="p-3 bg-[#F4F6F9] rounded-md border border-[#D8DDE6] text-center">
                    <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wide mb-1">Status</p>
                    <p className="text-[14px] font-bold text-[#2E844A]">Active</p>
                 </div>
                 <div className="p-3 bg-[#F4F6F9] rounded-md border border-[#D8DDE6] text-center">
                    <p className="text-[10px] font-bold text-[#706E6B] uppercase tracking-wide mb-1">SLA</p>
                    <p className="text-[14px] font-bold text-[#2E844A]">99.9%</p>
                 </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </DashboardModuleShell>
  );
};

export default MetaDirectMessagePage;
